import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertCircle, CheckCircle, Wallet, Building2, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage } from '@/lib/utils';

import { BACKEND_URL } from '@/lib/api';
const API = `${BACKEND_URL}/api`;

const BANK_ACCOUNTS = ["592", "OD", "Z-Del", "Z-Dine", "S-Del", "S-Dine", "Easy-Din", "G-Rabit", "Loan"];

const InitialSetup = ({ user, onComplete }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [setupStatus, setSetupStatus] = useState({ cash: false, bank: false });
  
  // Cash setup
  const [setupDate, setSetupDate] = useState('2026-01-01');
  const [openingCash, setOpeningCash] = useState('');
  
  // Bank setup
  const [bankBalances, setBankBalances] = useState({});

  useEffect(() => {
    checkSetupStatus();
  }, []);

  const checkSetupStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const [cashRes, bankRes] = await Promise.all([
        axios.get(`${API}/dayclosings/initial-setup-status`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/bank-opening-balances-status`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setSetupStatus({
        cash: !cashRes.data.needs_setup,
        bank: !bankRes.data.needs_setup
      });
      
      // If cash is already set, load the value
      if (cashRes.data.initial_config) {
        setOpeningCash(cashRes.data.initial_config.opening_cash?.toString() || '');
        setSetupDate(cashRes.data.initial_config.setup_date || '2026-01-01');
      }
      
      // If both are already set, notify parent
      if (!cashRes.data.needs_setup && !bankRes.data.needs_setup) {
        onComplete?.();
      }
    } catch (error) {
      console.error('Failed to check setup status');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCash = async () => {
    if (!openingCash || parseFloat(openingCash) < 0) {
      toast.error('Please enter a valid opening cash amount');
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/dayclosings/initial-opening-cash`, {
        date: setupDate,
        opening_cash: parseFloat(openingCash)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Opening cash saved successfully!');
      setSetupStatus(prev => ({ ...prev, cash: true }));
      setStep(2);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save opening cash'));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveBank = async () => {
    const validBalances = Object.entries(bankBalances)
      .filter(([_, val]) => val && parseFloat(val) > 0)
      .reduce((acc, [key, val]) => ({ ...acc, [key]: parseFloat(val) }), {});
    
    if (Object.keys(validBalances).length === 0) {
      toast.error('Please enter at least one bank balance');
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/bank-opening-balances`, {
        date: setupDate,
        balances: validBalances
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Bank opening balances saved successfully!');
      setSetupStatus(prev => ({ ...prev, bank: true }));
      onComplete?.();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save bank balances'));
    } finally {
      setSaving(false);
    }
  };

  const handleBankChange = (account, value) => {
    setBankBalances(prev => ({ ...prev, [account]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  // If both are already set, don't show setup
  if (setupStatus.cash && setupStatus.bank) {
    return null;
  }

  return (
    <div className="max-w-2xl mx-auto p-6">
      <Card className="border-2 border-blue-200 bg-blue-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-blue-800">
            <AlertCircle className="w-6 h-6" />
            Initial Setup Required
          </CardTitle>
          <CardDescription>
            Set up your opening balances for {setupDate} before starting daily operations.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Progress Steps */}
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${step === 1 ? 'bg-blue-600 text-white' : setupStatus.cash ? 'bg-green-100 text-green-700' : 'bg-gray-100'}`}>
              {setupStatus.cash ? <CheckCircle className="w-5 h-5" /> : <Wallet className="w-5 h-5" />}
              <span>Opening Cash</span>
            </div>
            <ArrowRight className="w-5 h-5 text-gray-400" />
            <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${step === 2 ? 'bg-blue-600 text-white' : setupStatus.bank ? 'bg-green-100 text-green-700' : 'bg-gray-100'}`}>
              {setupStatus.bank ? <CheckCircle className="w-5 h-5" /> : <Building2 className="w-5 h-5" />}
              <span>Bank Balances</span>
            </div>
          </div>

          {/* Step 1: Opening Cash */}
          {step === 1 && !setupStatus.cash && (
            <div className="space-y-4">
              <div className="p-4 bg-white rounded-lg border">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <Wallet className="w-5 h-5 text-blue-600" />
                  Set Opening Cash
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="setup-date">Setup Date</Label>
                    <Input
                      id="setup-date"
                      type="date"
                      value={setupDate}
                      onChange={(e) => setSetupDate(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="opening-cash">Opening Cash (₹)</Label>
                    <Input
                      id="opening-cash"
                      type="number"
                      placeholder="Enter opening cash"
                      value={openingCash}
                      onChange={(e) => setOpeningCash(e.target.value)}
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  This is the cash in hand at the start of {setupDate}. After this, opening cash will auto-fill from previous day's closing.
                </p>
              </div>
              <Button onClick={handleSaveCash} disabled={saving} className="w-full">
                {saving ? 'Saving...' : 'Save & Continue to Bank Setup'}
              </Button>
            </div>
          )}

          {/* Step 2: Bank Balances */}
          {(step === 2 || setupStatus.cash) && !setupStatus.bank && (
            <div className="space-y-4">
              <div className="p-4 bg-white rounded-lg border">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-blue-600" />
                  Set Bank Opening Balances
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  Enter the balance for each bank account as of {setupDate}. Leave empty or 0 for accounts with no balance.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {BANK_ACCOUNTS.map(account => (
                    <div key={account} className="flex items-center gap-2">
                      <Label className="w-24 font-medium">{account}</Label>
                      <Input
                        type="number"
                        placeholder="0.00"
                        value={bankBalances[account] || ''}
                        onChange={(e) => handleBankChange(account, e.target.value)}
                        min="0"
                        step="0.01"
                        className="flex-1"
                      />
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex gap-2">
                {!setupStatus.cash && (
                  <Button variant="outline" onClick={() => setStep(1)} className="flex-1">
                    Back
                  </Button>
                )}
                <Button onClick={handleSaveBank} disabled={saving} className="flex-1">
                  {saving ? 'Saving...' : 'Complete Setup'}
                </Button>
              </div>
            </div>
          )}

          {/* All Done */}
          {setupStatus.cash && setupStatus.bank && (
            <div className="text-center py-8">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-green-700">Setup Complete!</h3>
              <p className="text-gray-600">You can now start entering daily closings.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default InitialSetup;
